import { useCallback, useEffect, useState } from 'react';

import type { AnsibleVariable, Playbook, PlaybookType } from '@/api/configuration';
import {
    createPlaybook,
    deletePlaybook,
    listPlaybooks,
    updatePlaybook,
} from '@/api/configuration';
import { useToastStore } from '@/stores/toast-store';

import { DefinitionSelector } from './definition-selector';

// --- Constants ---

const PLAYBOOK_TYPES: PlaybookType[] = [
  'DEPLOY',
  'CONFIGURATION',
  'RUN_DB_SCRIPT',
  'SERVICE_ACTION',
  'SERVICE_STATUS',
  'UTILITY',
  'SYSTEM_BUILD',
  'SERVER_UP',
  'REBOOT',
  'NONE',
];

const PLAYBOOK_TYPE_LABELS: Record<PlaybookType, string> = {
  DEPLOY: 'Deploy',
  CONFIGURATION: 'Configuration',
  RUN_DB_SCRIPT: 'Run DB Script',
  SERVICE_ACTION: 'Service Action',
  SERVICE_STATUS: 'Service Status',
  UTILITY: 'Utility',
  SYSTEM_BUILD: 'System Build',
  SERVER_UP: 'Server Up',
  REBOOT: 'Reboot',
  NONE: 'None',
};

/** Deployment types that define what technology stack a playbook targets. */
const DEPLOYMENT_TYPES = [
  'SPRING',
  'TOMCAT',
  'WEBAPP',
  'JBOSS',
  'OSGI',
  'NODE',
  'CUSTOM',
] as const;

type DeploymentType = (typeof DEPLOYMENT_TYPES)[number];

const DEPLOYMENT_TYPE_LABELS: Record<DeploymentType, string> = {
  SPRING: 'Spring Boot',
  TOMCAT: 'Tomcat',
  WEBAPP: 'Web Application',
  JBOSS: 'JBoss',
  OSGI: 'OSGi',
  NODE: 'Node.js',
  CUSTOM: 'Custom',
};

// --- Key-Value Editor ---

function KeyValueEditor({
  variables,
  onChange,
}: {
  variables: AnsibleVariable[];
  onChange: (vars: AnsibleVariable[]) => void;
}) {
  const handleAdd = () => {
    onChange([...variables, { key: '', value: '' }]);
  };

  const handleRemove = (index: number) => {
    onChange(variables.filter((_, i) => i !== index));
  };

  const handleChange = (index: number, field: 'key' | 'value', val: string) => {
    const updated = variables.map((v, i) =>
      i === index ? { ...v, [field]: val } : v
    );
    onChange(updated);
  };

  return (
    <div className="space-y-2">
      {variables.map((variable, index) => (
        <div key={index} className="flex gap-2 items-center">
          <input
            type="text"
            value={variable.key}
            onChange={(e) => handleChange(index, 'key', e.target.value)}
            placeholder="Key"
            className="flex-1 rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-content focus:outline-none focus:ring-2 focus:ring-brand-600"
          />
          <input
            type="text"
            value={variable.value}
            onChange={(e) => handleChange(index, 'value', e.target.value)}
            placeholder="Value"
            className="flex-1 rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-content focus:outline-none focus:ring-2 focus:ring-brand-600"
          />
          <button
            type="button"
            onClick={() => handleRemove(index)}
            className="rounded px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1"
          aria-label={`Remove variable ${variable.key || String(index + 1)}`}
          >
            ✕
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={handleAdd}
        className="text-xs font-medium text-brand-700 hover:text-brand-800 dark:text-brand-400 dark:hover:text-brand-300"
      >
        + Add Variable
      </button>
    </div>
  );
}

// --- Playbook Form ---

interface PlaybookFormProps {
  initial?: Playbook;
  onSave: (data: Omit<Playbook, 'id' | 'projectKey'>) => void;
  onCancel: () => void;
}

function PlaybookForm({ initial, onSave, onCancel }: PlaybookFormProps) {
  const [name, setName] = useState(initial?.name ?? '');
  const [playbookType, setPlaybookType] = useState<PlaybookType>(initial?.playbookType ?? 'DEPLOY');
  const [deploymentType, setDeploymentType] = useState<DeploymentType | ''>(
    initial?.playbookType === 'DEPLOY' && initial?.name
      ? (DEPLOYMENT_TYPES.includes(initial.name as DeploymentType) ? initial.name as DeploymentType : '')
      : ''
  );
  const [location, setLocation] = useState(initial?.location ?? '');
  const [definitionId, setDefinitionId] = useState<number | null>(initial?.definitionId ?? null);
  const [defaultVars, setDefaultVars] = useState<AnsibleVariable[]>(initial?.defaultVars ?? []);
  const [errors, setErrors] = useState<{ name?: string; playbookType?: string }>({});

  const isLocationDisabled = definitionId != null;

  const handleDefinitionChange = (selectedId: number | null, path: string | null) => {
    setDefinitionId(selectedId);
    if (selectedId != null && path != null) {
      // Definition selected: auto-populate location and it will be disabled
      setLocation(path);
    }
    // When cleared (selectedId === null), location remains editable with its current value
  };

  const validate = (): boolean => {
    const newErrors: { name?: string; playbookType?: string } = {};
    if (playbookType === 'DEPLOY') {
      if (!deploymentType) {
        newErrors.name = 'Deployment type is required';
      }
    } else if (!name.trim()) {
      newErrors.name = 'Name is required';
    }
    if (!PLAYBOOK_TYPES.includes(playbookType)) {
      newErrors.playbookType = 'Invalid playbook type';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    onSave({
      name: name.trim(),
      playbookType,
      location: location.trim(),
      definitionId,
      defaultVars: defaultVars.filter((v) => v.key.trim() !== ''),
    });
  };

  return (
    <form onSubmit={handleSubmit} className="rounded-lg border border-border bg-surface-raised p-4 space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label htmlFor="pb-type" className="block text-xs font-medium text-content-secondary mb-1">
            Playbook Type *
          </label>
          <select
            id="pb-type"
            value={playbookType}
            onChange={(e) => {
              const newType = e.target.value as PlaybookType;
              setPlaybookType(newType);
              if (newType === 'DEPLOY') {
                setDeploymentType('' as DeploymentType | '');
                setName('');
              } else {
                setDeploymentType('');
              }
            }}
            className="w-full rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-content focus:outline-none focus:ring-2 focus:ring-brand-600"
          >
            {PLAYBOOK_TYPES.map((t) => (
              <option key={t} value={t}>{PLAYBOOK_TYPE_LABELS[t]}</option>
            ))}
          </select>
          {errors.playbookType && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.playbookType}</p>}
        </div>
        <div>
          {playbookType === 'DEPLOY' ? (
            <>
              <label htmlFor="pb-deployment-type" className="block text-xs font-medium text-content-secondary mb-1">
                Deployment Type *
              </label>
              <select
                id="pb-deployment-type"
                value={deploymentType}
                onChange={(e) => {
                  const val = e.target.value as DeploymentType;
                  setDeploymentType(val);
                  setName(val);
                }}
                className="w-full rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-content focus:outline-none focus:ring-2 focus:ring-brand-600"
              >
                <option value="">--- Select deployment type ---</option>
                {DEPLOYMENT_TYPES.map((dt) => (
                  <option key={dt} value={dt}>{DEPLOYMENT_TYPE_LABELS[dt]}</option>
                ))}
              </select>
              {errors.name && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.name}</p>}
            </>
          ) : (
            <>
              <label htmlFor="pb-name" className="block text-xs font-medium text-content-secondary mb-1">
                Name *
              </label>
              <input
                id="pb-name"
                type="text"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. restart-services"
                className="w-full rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-content focus:outline-none focus:ring-2 focus:ring-brand-600"
              />
              {errors.name && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.name}</p>}
            </>
          )}
        </div>
        <div className="sm:col-span-2">
          <DefinitionSelector
            value={definitionId}
            playbookType={playbookType}
            onChange={handleDefinitionChange}
          />
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="pb-location" className="block text-xs font-medium text-content-secondary mb-1">
            Location <span className="text-content-tertiary">(path to playbook file)</span>
          </label>
          <input
            id="pb-location"
            type="text"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            disabled={isLocationDisabled}
            placeholder="deploy/tomcat-deploy.yml"
            className="w-full rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-content focus:outline-none focus:ring-2 focus:ring-brand-600 disabled:opacity-50 disabled:cursor-not-allowed"
          />
          {isLocationDisabled && (
            <p className="mt-1 text-xs text-content-tertiary">
              Path is set by the selected definition. Clear the definition to enter manually.
            </p>
          )}
        </div>
      </div>

      <div>
        <span className="block text-xs font-medium text-content-secondary mb-1">Default Variables</span>
        <KeyValueEditor variables={defaultVars} onChange={setDefaultVars} />
      </div>

      <div className="flex gap-2 pt-1">
        <button type="submit" className="rounded-md bg-brand-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-800 focus:outline-none focus:ring-2 focus:ring-brand-600 focus:ring-offset-2">
          {initial ? 'Update' : 'Add Playbook'}
        </button>
        <button type="button" onClick={onCancel} className="rounded-md border border-border px-3 py-1.5 text-xs font-medium text-content-secondary hover:bg-surface-raised focus:outline-none focus:ring-2 focus:ring-brand-600 focus:ring-offset-2">
          Cancel
        </button>
      </div>
    </form>
  );
}

// --- Collapsible Section ---

function CollapsibleSection({
  title,
  count,
  children,
}: {
  title: string;
  count: number;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(true);

  return (
    <div className="rounded-lg border border-border">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium text-content hover:bg-surface-raised transition-colors rounded-t-lg"
        aria-expanded={open}
      >
        <span>{title} ({count})</span>
        <svg
          className={`h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && <div className="border-t border-border">{children}</div>}
    </div>
  );
}

// --- Main Component ---

interface PlaybookManagerProps {
  projectKey: string;
}

export function PlaybookManager({ projectKey }: PlaybookManagerProps) {
  const [playbooks, setPlaybooks] = useState<Playbook[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Playbook | null>(null);
  const addToast = useToastStore((s) => s.addToast);

  const loadPlaybooks = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listPlaybooks(projectKey);
      setPlaybooks(data);
    } catch {
      setError('Failed to load playbooks.');
    } finally {
      setLoading(false);
    }
  }, [projectKey]);

  useEffect(() => {
    if (projectKey) {
      loadPlaybooks();
    }
  }, [projectKey, loadPlaybooks]);

  const handleAdd = async (data: Omit<Playbook, 'id' | 'projectKey'>) => {
    try {
      await createPlaybook(projectKey, data);
      setShowForm(false);
      addToast({ type: 'success', message: 'Playbook created successfully', duration: 5000 });
      loadPlaybooks();
    } catch {
      addToast({ type: 'error', message: 'Failed to create playbook', duration: 5000 });
    }
  };

  const handleEdit = async (data: Omit<Playbook, 'id' | 'projectKey'>) => {
    if (!editing?.id) return;
    try {
      await updatePlaybook(projectKey, editing.id, data);
      setEditing(null);
      addToast({ type: 'success', message: 'Playbook updated successfully', duration: 5000 });
      loadPlaybooks();
    } catch {
      addToast({ type: 'error', message: 'Failed to update playbook', duration: 5000 });
    }
  };

  const handleDelete = async (playbook: Playbook) => {
    if (!playbook.id) return;
    if (!confirm(`Delete playbook "${playbook.name}"?`)) return;
    try {
      await deletePlaybook(projectKey, playbook.id);
      addToast({ type: 'success', message: 'Playbook deleted successfully', duration: 5000 });
      loadPlaybooks();
    } catch {
      addToast({ type: 'error', message: 'Failed to delete playbook', duration: 5000 });
    }
  };

  // Group playbooks by type
  const grouped = playbooks.reduce<Record<PlaybookType, Playbook[]>>((acc, pb) => {
    if (!acc[pb.playbookType]) {
      acc[pb.playbookType] = [];
    }
    acc[pb.playbookType].push(pb);
    return acc;
  }, {} as Record<PlaybookType, Playbook[]>);

  const typesWithPlaybooks = PLAYBOOK_TYPES.filter((t) => grouped[t]?.length > 0);

  return (
    <section className="mt-6">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-content">Playbooks</h3>
        {!showForm && !editing && (
          <button
            type="button"
            onClick={() => setShowForm(true)}
            className="inline-flex items-center gap-1.5 rounded-md bg-accent-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-accent-800 focus:outline-none focus:ring-2 focus:ring-accent-600 focus:ring-offset-2 transition-colors"
          >
            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Playbook
          </button>
        )}
      </div>

      {error && (
        <div role="alert" className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-3 mb-3">
          <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
          <button onClick={() => { setError(null); loadPlaybooks(); }} className="mt-1 text-xs font-medium text-red-700 dark:text-red-300 underline">Retry</button>
        </div>
      )}

      {loading && (
        <div className="animate-pulse space-y-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-10 rounded-lg bg-gray-200 dark:bg-gray-700" />
          ))}
        </div>
      )}

      {showForm && (
        <div className="mb-3">
          <PlaybookForm onSave={handleAdd} onCancel={() => setShowForm(false)} />
        </div>
      )}

      {!loading && playbooks.length === 0 && !showForm && (
        <div className="rounded-lg border border-dashed border-border p-4 text-center">
          <p className="text-sm text-content-tertiary">No playbooks configured. Add one to enable deployment automation.</p>
        </div>
      )}

      {!loading && typesWithPlaybooks.length > 0 && (
        <div className="space-y-3">
          {typesWithPlaybooks.map((type) => (
            <CollapsibleSection key={type} title={PLAYBOOK_TYPE_LABELS[type]} count={grouped[type].length}>
              <ul className="divide-y divide-border">
                {grouped[type].map((pb) =>
                  editing?.id === pb.id ? (
                    <li key={pb.id} className="p-3">
                      <PlaybookForm
                        initial={pb}
                        onSave={handleEdit}
                        onCancel={() => setEditing(null)}
                      />
                    </li>
                  ) : (
                    <li key={pb.id} className="p-3 flex items-center justify-between">
                      <div>
                        <span className="text-sm font-medium text-content">{pb.playbookType === 'DEPLOY' && DEPLOYMENT_TYPES.includes(pb.name as DeploymentType) ? DEPLOYMENT_TYPE_LABELS[pb.name as DeploymentType] : pb.name}</span>
                        {pb.playbookType === 'DEPLOY' && DEPLOYMENT_TYPES.includes(pb.name as DeploymentType) && (
                          <span className="ml-2 inline-flex items-center rounded-full bg-emerald-100 dark:bg-emerald-900/30 px-2 py-0.5 text-xs font-medium text-emerald-700 dark:text-emerald-300">
                            {pb.name}
                          </span>
                        )}
                        <span className="ml-2 text-xs text-content-tertiary font-mono">{pb.location}</span>
                        {pb.definitionId != null && (
                          <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-brand-100 dark:bg-brand-900/30 px-2 py-0.5 text-xs font-medium text-brand-700 dark:text-brand-300">
                            &#x1F4CB; Linked to definition
                          </span>
                        )}
                        {pb.defaultVars && pb.defaultVars.length > 0 && (
                          <span className="ml-2 text-xs text-content-secondary">
                            ({pb.defaultVars.length} var{pb.defaultVars.length !== 1 ? 's' : ''})
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => setEditing(pb)}
                          className="rounded px-2 py-1 text-xs font-medium text-content-secondary hover:bg-surface-raised focus:outline-none focus:ring-2 focus:ring-brand-600 focus:ring-offset-1"
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(pb)}
                          className="rounded px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1"
                        >
                          Remove
                        </button>
                      </div>
                    </li>
                  )
                )}
              </ul>
            </CollapsibleSection>
          ))}
        </div>
      )}
    </section>
  );
}